import { Check } from "@material-symbols-svg/react/rounded/icons/check";
import { Error as ErrorIcon } from "@material-symbols-svg/react/rounded/icons/error";
import { ProgressActivity } from "@material-symbols-svg/react/rounded/icons/progress-activity";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { appUrlFor, resolveBrand } from "@/shared/brand";
import { humanizeAuthError, useActionAuth, useGcipAuth } from "./auth";
import { AuthCard } from "./features/auth/auth-card";
import { AuthStatusPanel } from "./features/auth/auth-status-panel";
import { ChangePasswordScreen } from "./features/auth/change-password-screen";
import {
  clearStashedEmail,
  stashResetEmail,
} from "./features/auth/last-email-store";
import { ResetPasswordScreen } from "./features/auth/reset-password-screen";
import { SignInForm } from "./features/auth/sign-in-form";
import { SmallScreenNotice } from "./features/auth/small-screen-notice";
import { useSmallScreenNotice } from "./features/auth/use-small-screen-notice";
import { GradientBackground } from "./shared/components/gradient-background";

// Reads the email-link action params (Identity Platform sends them on the
// reset-password URL the user clicks in their inbox). When present, render the
// action handler instead of the IAP sign-in flow.
type ActionParams = {
  mode: string;
  oobCode: string;
  apiKey?: string;
  tenantId?: string;
  lang?: string;
};

function readActionParams(): ActionParams | null {
  const p = new URLSearchParams(window.location.search);
  const mode = p.get("mode");
  const oobCode = p.get("oobCode");
  if (!mode || !oobCode) return null;
  return {
    mode,
    oobCode,
    apiKey: p.get("apiKey") || undefined,
    tenantId: p.get("tenantId") || undefined,
    lang: p.get("lang") || undefined,
  };
}

// IAP sign-out redirect (?mode=signout, never an oobCode — email action links
// always carry one). By protocol this URL has NO tenant id: sign-out is not
// tenant-scoped, IAP appends only apiKey/state.
function isSignOutRequest(): boolean {
  const p = new URLSearchParams(window.location.search);
  return p.get("mode") === "signout" && !p.get("oobCode");
}

/** Shared visual shell: fullscreen gradient, centered card. Help/legal links
 *  live in the app (after the IAP redirect), not on the sign-in page. */
function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <GradientBackground
      animated
      className="flex min-h-svh items-center justify-center p-6"
    >
      {children}
    </GradientBackground>
  );
}

/** Minimal operational-state card (loading / connectivity errors). These are
 *  gcip-iap glue states, not design-system screens, so they use plain German
 *  copy like the rest of the glue (humanizeAuthError). */
function InfoCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <AuthCard>
      <div className="flex flex-col gap-3">
        <h1 className="text-heading-medium tracking-[-0.56px] text-foreground">
          {title}
        </h1>
        <p className="text-label-medium text-muted-foreground">{children}</p>
      </div>
    </AuthCard>
  );
}

export default function App() {
  // Capture once at mount; treating the route as immutable per page-load avoids
  // having to re-route mid-session.
  const brand = useMemo(resolveBrand, []);
  const action = useMemo(readActionParams, []);
  const signOut = useMemo(isSignOutRequest, []);

  // "Back to login" target: the tenant's IAP-protected app URL. Navigating
  // there makes IAP start a fresh, correctly parameterized login redirect —
  // the page cannot self-initiate one (stripping the params and reloading
  // breaks gcip-iap with "Invalid mode"). Null (no known tenant) hides the
  // button instead of guessing.
  const appUrl = useMemo(() => {
    const fromTenant = brand.ok ? appUrlFor(brand.tenantId) : null;
    if (fromTenant) return fromTenant;
    return import.meta.env.DEV ? DEV_APP_URL : null;
  }, [brand]);

  // Sign-out routes BEFORE the fail-closed brand guard: its URL carries no
  // tenant id (see isSignOutRequest), so brand resolution cannot succeed —
  // yet gcip-iap must run to clear the GCIP browser session and return to
  // IAP. Served brand-neutrally; normally only the spinner is visible. DEV
  // has no IAP context, so it previews the final confirmation card instead.
  if (signOut) {
    return (
      <AuthLayout>
        {import.meta.env.DEV ? <SignedOutConfirmation /> : <SignOutFlow />}
      </AuthLayout>
    );
  }

  // Fail closed (ADR-0010): no tenant means no brand, an unknown tenant is
  // refused — for sign-in redirects AND action links alike. Only the IAP
  // sign-out above is exempt: its URL never carries a tenant id.
  if (!brand.ok) {
    return (
      <AuthLayout>
        <AuthStatusPanel
          icon={ErrorIcon}
          title="Anmeldung nicht möglich"
          description={
            brand.reason === "unknown-tenant"
              ? "Dieser Link gehört zu keiner bekannten Lernwelt-Umgebung. Bitte fordere einen neuen Link an oder wende dich an den Support."
              : "Diese Seite kann nicht direkt geöffnet werden. Bitte rufe die Lernwelt auf — du wirst automatisch zur Anmeldung weitergeleitet — oder nutze einen gültigen Link aus einer E-Mail."
          }
        />
      </AuthLayout>
    );
  }

  if (action && action.mode === "resetPassword") {
    return (
      <AuthLayout>
        <ResetPasswordAction action={action} appUrl={appUrl} />
      </AuthLayout>
    );
  }

  if (action) {
    // Unknown action mode — friendly fallback rather than failing silently
    // inside gcip-iap.
    return (
      <AuthLayout>
        <AuthStatusPanel
          icon={ErrorIcon}
          title="Aktion nicht unterstützt"
          description="Diese Seite unterstützt aktuell nur das Zurücksetzen von Passwörtern. Bitte öffne den Login direkt."
        >
          {appUrl ? <AuthSubmitButtonBackToLogin appUrl={appUrl} /> : null}
        </AuthStatusPanel>
      </AuthLayout>
    );
  }

  // Local dev preview: the gcip-iap handshake needs IAP redirect params
  // (apiKey/tenantId), which don't exist on the Vite dev server. Render the
  // masks with stub handlers so the UI/branding are reviewable locally.
  // import.meta.env.DEV is statically false in `vite build`, so this branch is
  // dead-code-eliminated from the production bundle.
  if (import.meta.env.DEV) {
    return (
      <AuthLayout>
        <PreviewSignInFlow />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <SignInFlow />
    </AuthLayout>
  );
}

// Where "sign in" hands off in local dev (no IAP). Defaults to the local
// Lernwelt frontend; overridable via VITE_APP_URL. `?devlogin=1` tells the
// frontend's dev-only auto-login to establish the BFF dev session, so you land
// in the app with the seeded user + deal — the local stand-in for the IAP
// redirect-back. Stripped from production builds (this whole branch is DEV-only).
const DEV_APP_URL = import.meta.env.VITE_APP_URL ?? "http://lernwelt.c1g.local";

/** Local-only: renders the sign-in / reset masks without gcip-iap, so the
 *  polished UI is viewable on the Vite dev server (no IAP context). "Sign in"
 *  hands off to the frontend's dev auto-login (see DEV_APP_URL). */
function PreviewSignInFlow() {
  const [view, setView] = useState<"sign-in" | "reset-password">("sign-in");
  const smallScreen = useSmallScreenNotice();

  async function previewSignIn() {
    // No credential check locally (no IAP/GCIP); hand off to the frontend,
    // which performs the dev-login against the BFF and drops you into the app.
    window.location.href = `${DEV_APP_URL}?devlogin=1`;
    // The page navigates away; never resolve so the form stays "submitting".
    return new Promise<never>(() => {});
  }
  async function previewRequestReset() {
    // Resolve so the "email sent" confirmation panel is reviewable too.
  }

  if (smallScreen) return <SmallScreenNotice />;

  if (view === "reset-password") {
    return (
      <ResetPasswordScreen
        onRequestReset={previewRequestReset}
        onBack={() => setView("sign-in")}
      />
    );
  }
  return (
    <SignInForm
      onSignIn={previewSignIn}
      onForgotPassword={() => setView("reset-password")}
    />
  );
}

/** Primary-styled "back to login" button reused across fallback panels;
 *  navigates to the tenant's app URL so IAP restarts the login. */
function AuthSubmitButtonBackToLogin({ appUrl }: { appUrl: string }) {
  return (
    <button
      type="button"
      onClick={() => window.location.assign(appUrl)}
      className="h-10 w-full rounded-full bg-primary text-label-small text-primary-foreground"
    >
      Zurück zum Login
    </button>
  );
}

/** IAP sign-out glue (?mode=signout): mounting useGcipAuth runs the gcip-iap
 *  sign-out — GCIP sessions are cleared, then the library redirects back to
 *  IAP via `state` (the normal app logout; only the spinner shows). Without
 *  `state` it calls completeSignOut() → confirmation card. Brand-neutral:
 *  the sign-out URL carries no tenant id (see App). */
function SignOutFlow() {
  const { status, error: initError } = useGcipAuth();

  if (status === "signedOut") return <SignedOutConfirmation />;

  if (status === "error") {
    return (
      <AuthStatusPanel
        icon={ErrorIcon}
        title="Abmeldung nicht möglich"
        description={
          initError ??
          "Die Abmeldung konnte nicht abgeschlossen werden. Bitte lade die Seite neu."
        }
      />
    );
  }

  return (
    <InfoCard title="Abmelden">
      <span className="inline-flex items-center gap-2">
        <ProgressActivity className="size-4 animate-spin" aria-hidden />
        Du wirst abgemeldet…
      </span>
    </InfoCard>
  );
}

/** Final sign-out state when there is no IAP redirect to return to. No
 *  back-to-login button: without a tenant id there is no app URL to target. */
function SignedOutConfirmation() {
  return (
    <AuthStatusPanel
      icon={Check}
      title="Du wurdest abgemeldet"
      description="Um dich wieder anzumelden, rufe die Lernwelt einfach erneut auf."
    />
  );
}

/** IAP sign-in flow: sign-in card with a toggle to the reset-password card. */
function SignInFlow() {
  const { status, error: initError, signIn, sendPasswordReset } = useGcipAuth();
  const [view, setView] = useState<"sign-in" | "reset-password">("sign-in");
  const smallScreen = useSmallScreenNotice();

  // Map Firebase errors to display-ready messages here so the screens stay
  // transport-agnostic (they just render error.message).
  async function handleSignIn(email: string, password: string) {
    let result: unknown;
    try {
      result = await signIn(email, password);
    } catch (err) {
      throw new Error(humanizeAuthError(err));
    }
    // Signed in — the post-reset prefill hand-off (if any) is complete.
    clearStashedEmail();
    return result;
  }

  // Enumeration-safe: unknown emails resolve as success (no leak); only
  // client/network errors surface (with a humanized message).
  async function handleRequestReset(email: string) {
    try {
      await sendPasswordReset(email);
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      if (
        code === "auth/invalid-email" ||
        code === "auth/missing-email" ||
        code === "auth/network-request-failed" ||
        code === "auth/too-many-requests"
      ) {
        throw new Error(humanizeAuthError(err));
      }
      // user-not-found and anything unexpected: treat as success.
    }
  }

  if (status === "loading") {
    return (
      <InfoCard title="In der Lernwelt anmelden">
        Anmeldung wird vorbereitet…
      </InfoCard>
    );
  }

  if (status === "error") {
    return (
      <AuthStatusPanel
        icon={ErrorIcon}
        title="Anmeldung nicht möglich"
        description={
          initError ??
          "Die Anmeldung konnte nicht vorbereitet werden. Bitte lade die Seite neu."
        }
      />
    );
  }

  if (smallScreen) return <SmallScreenNotice />;

  if (view === "reset-password") {
    return (
      <ResetPasswordScreen
        onRequestReset={handleRequestReset}
        onBack={() => setView("sign-in")}
      />
    );
  }

  return (
    <SignInForm
      onSignIn={handleSignIn}
      onForgotPassword={() => setView("reset-password")}
    />
  );
}

/** Email-action handler for password reset (opened from the reset link;
 *  NOT behind IAP, so it uses useActionAuth rather than gcip-iap). */
function ResetPasswordAction({
  action,
  appUrl,
}: {
  action: ActionParams;
  appUrl: string | null;
}) {
  const actionAuth = useActionAuth({
    apiKey: action.apiKey,
    tenantId: action.tenantId,
  });
  const auth = actionAuth.auth;
  const initError: string | null = actionAuth.error
    ? humanizeAuthError(actionAuth.error)
    : null;

  // One value per phase, carrying what that phase needs: the form exists only
  // with the Auth instance the code was verified on and the account email it
  // was issued for, and the invalid screen only with its message. Kept
  // apart, each render would have to guard against a combination the flow
  // never produces.
  const [phase, setPhase] = useState<ResetPhase>({ kind: "verifying" });
  const smallScreen = useSmallScreenNotice();

  useEffect(() => {
    if (!auth) return;
    let cancelled = false;
    (async () => {
      try {
        // Validates the oobCode and returns the email it was issued for.
        const email = await auth.verifyPasswordResetCode(action.oobCode);
        if (!cancelled) setPhase({ kind: "ready", auth, email });
      } catch (err) {
        if (!cancelled) {
          setPhase({ kind: "invalid", message: humanizeAuthError(err) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, action.oobCode]);

  // The email is stashed after a confirmed reset so the next sign-in (a
  // separate page load, reached via app URL → IAP redirect) can prefill it.
  async function confirmWith(ready: ResetReady, newPassword: string) {
    try {
      await ready.auth.confirmPasswordReset(action.oobCode, newPassword);
    } catch (err) {
      throw new Error(humanizeAuthError(err));
    }
    stashResetEmail(ready.email);
  }

  if (initError) {
    return (
      <AuthStatusPanel
        icon={ErrorIcon}
        title="Passwort zurücksetzen"
        description={initError}
      />
    );
  }

  if (phase.kind === "verifying") {
    return (
      <InfoCard title="Passwort zurücksetzen">
        <span className="inline-flex items-center gap-2">
          <ProgressActivity className="size-4 animate-spin" aria-hidden />
          Link wird geprüft…
        </span>
      </InfoCard>
    );
  }

  if (phase.kind === "invalid") {
    return (
      <AuthStatusPanel
        icon={ErrorIcon}
        title="Link nicht mehr gültig"
        description={phase.message}
      >
        {appUrl ? <AuthSubmitButtonBackToLogin appUrl={appUrl} /> : null}
      </AuthStatusPanel>
    );
  }

  if (smallScreen) return <SmallScreenNotice />;

  return (
    <ChangePasswordScreen
      onConfirm={(newPassword) => confirmWith(phase, newPassword)}
      onBackToLogin={appUrl ? () => window.location.assign(appUrl) : undefined}
    />
  );
}

type ActionAuth = NonNullable<ReturnType<typeof useActionAuth>["auth"]>;

type ResetReady = { kind: "ready"; auth: ActionAuth; email: string };

type ResetPhase =
  | { kind: "verifying" }
  | ResetReady
  | { kind: "invalid"; message: string };
